import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "duration", "direction", "in", "from", "to", "hiddenVisibility", "onStart", "onEnd", "keepMounted", "style"];
import React, { forwardRef, useRef, useCallback } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import { durations, directions, convertUnitToInt, isFunction } from '../helper';
import { useMergeRefs } from '../hooks';

const resolveTransition = (direction, fromProp, toProp, forwardedTransform) => {
  let from, to;

  switch (direction) {
    case directions.vertical:
      from = "translateY(".concat(fromProp, "px)");
      to = "translateY(".concat(toProp, "px)");
      break;

    case directions.horizontal:
      from = "translateX(".concat(fromProp, "px)");
      to = "translateX(".concat(toProp, "px)");
      break;

    default:
      from = "translate3d(0, 0, 0)";
      to = "translate3d(0, 0, 0)";
  }

  return {
    from: "".concat(from, " ").concat(forwardedTransform),
    to: "".concat(to, " ").concat(forwardedTransform)
  };
};

const Slide = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    duration: durationProp,
    direction,
    in: inProp,
    from: fromProp,
    to: toProp,
    hiddenVisibility,
    onStart,
    onEnd,
    keepMounted,
    style
  } = props,
        rest = _objectWithoutPropertiesLoose(props, _excluded);

  const childrenRef = useRef(null);
  const handleOwnRef = useCallback(instance => {
    childrenRef.current = findDOMNode(instance);
  }, []);
  const handleRef = useMergeRefs([handleOwnRef, children.ref, ref]);
  const childrenTransformStyle = children.props.style && children.props.style.hasOwnProperty('transform') ? children.props.style.transform : '';
  const {
    from,
    to
  } = resolveTransition(direction, fromProp, toProp, childrenTransformStyle);

  const handleEnd = () => isFunction(onEnd) && onEnd({
    target: childrenRef.current
  });

  const handleStart = () => isFunction(onStart) && onStart({
    target: childrenRef.current
  });

  const duration = convertUnitToInt(durationProp, 'ms');
  const defaultStyle = {
    willChange: 'transform',
    transition: "transform ".concat(duration, "ms var(--motion-timing-function-ease-in-out)"),
    transform: from
  };
  let timeout = duration;
  let transitionStyles = {
    entering: {
      transform: to
    },
    entered: {
      transform: to
    },
    exiting: {
      transform: from
    },
    exited: {
      transform: from
    }
  };

  if (!keepMounted) {
    transitionStyles = {
      entering: {
        transform: from
      },
      entered: {
        transform: to
      },
      exiting: {
        transform: from
      },
      exited: {
        transform: from
      }
    };
    timeout = {
      enter: 0,
      exit: duration
    };
  }

  const childrenStyle = /*#__PURE__*/React.isValidElement(children) && children.props ? children.props.style : {};
  return /*#__PURE__*/React.createElement(Transition, _extends({
    mountOnEnter: !keepMounted,
    unmountOnExit: !keepMounted,
    nodeRef: childrenRef,
    onEnter: handleStart,
    onEntered: handleEnd,
    onExit: handleStart,
    onExited: handleEnd,
    in: inProp,
    timeout: timeout,
    appear: true
  }, rest), (state, childProps) => {
    return /*#__PURE__*/React.cloneElement(children, Object.assign({
      ref: handleRef,
      style: Object.assign({
        visibility: hiddenVisibility && state === 'exited' && !inProp ? 'hidden' : undefined
      }, style, defaultStyle, childrenStyle, transitionStyles[state])
    }, childProps));
  });
});
Slide.displayName = 'Slide';
Slide.directions = {
  vertical: directions.vertical,
  horizontal: directions.horizontal
};
Slide.durations = {
  extraFast: durations.extraFast,
  fast: durations.fast,
  normal: durations.normal,
  slow: durations.slow,
  extraSlow: durations.extraSlow,
  ultraSlow: durations.ultraSlow
};
Slide.defaultProps = {
  children: null,
  direction: Slide.directions.horizontal,
  duration: Slide.durations.normal,
  from: 0,
  to: 0,
  hiddenVisibility: false,
  keepMounted: true
};
process.env.NODE_ENV !== "production" ? Slide.propTypes = {
  /**
   * Sets the children.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType]),

  /**
   * Direction the child node will enter from.
   */
  direction: PropTypes.oneOf([Slide.directions.vertical, Slide.directions.horizontal]),

  /**
   * Sets the durations of the transition between from and to values.
   */
  duration: PropTypes.oneOf([Slide.durations.ultraSlow, Slide.durations.extraSlow, Slide.durations.slow, Slide.durations.normal, Slide.durations.fast, Slide.durations.extraFast]),

  /**
   * Sets when transition ends and visibility is hidden.
   */
  hiddenVisibility: PropTypes.bool,

  /**
   * Keep the component mounted before and after the animation.
   */
  keepMounted: PropTypes.bool,

  /**
   * Sets the pixel position to define the start point of animation. Negative number are allowed.
   */
  from: PropTypes.number,

  /**
   * Triggers the enter or exit states.
   */
  in: PropTypes.bool,

  /**
   * Sets the callback when animation start.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   *
   */
  onStart: PropTypes.func,

  /**
   * Sets the callback when animation end.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   *
   */
  onEnd: PropTypes.func,

  /**
   * Sets the pixel position to define the end point of animation. Negative number are allowed.
   */
  to: PropTypes.number
} : void 0;
export default Slide;